import useUrlState from '@ahooksjs/use-url-state';
import { CloseOutlined, DownOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { DefaultOptionType } from '@bugly/common';
import { useControllableValue } from 'ahooks';
import {
  Form,
  FormInstance,
  Input,
  ModalProps,
  Select,
  Space,
  Tooltip,
  SelectProps,
  InputNumber,
  Dropdown,
  Button,
} from 'antd';
import classNames from 'classnames';
import { isEmpty, isNil, isNumber, isString, remove } from 'lodash-es';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MATCH_MODE_TEXT, MatchMode } from '../../form/standards/types';
import { TdsInput } from '../tds-input';
import { TdsInputNumberRange } from '../tds-number-range';
import {
  BaseFormItemProps,
  OptionType,
  PropsWithStyles,
} from '../type';
import styles from './index.module.scss';

export type FilterValue = string | number;

export interface TdsCustomFieldsValue {
  name?: string;
  operator?: MatchMode;
  values?: string | number;
  unit?: string;
}

export type TdsCustomFieldsItemProps = {
  index: number;
  onDelete?: (index: number) => void;
  form: FormInstance;
  onChange?: (value: TdsCustomFieldsValue, index: number) => void;
  operatorOptions?: OptionType[];
  metricOptions?: OptionType[];
  unitOptions?: OptionType[];
  fieldsValue?: any;
};

export interface TdsFieldSelectorProps extends SelectProps {
  forceRemote?: boolean;
  getOptions?: () => Promise<DefaultOptionType[]>;
}

export interface TdsCustomFieldsProps extends BaseFormItemProps<TdsCustomFieldsValue[]> {
  modalProps?: ModalProps;
  metricSelectProps?: TdsFieldSelectorProps;
  operatorOptions?: OptionType[];
  unitOptions?: OptionType[];
};

const CustomFieldsItem = (props: TdsCustomFieldsItemProps) => {
  const { form, index, metricOptions = [], operatorOptions = [], unitOptions = [], fieldsValue = {} } = props;
  const [mode, setMode] = useState('');

  const onDelete = () => {
    props.onDelete?.(props.index);
  };

  const getUnit = (fieldsValue: Record<string, any>) => unitOptions
    ?.find(option => option.fieldKey === fieldsValue?.name)?.value || unitOptions[0]?.value || '';

  const onFieldsChange = () => {
    const fieldsValue = form.getFieldsValue();
    form.setFieldsValue({
      ...fieldsValue,
      unit: getUnit(fieldsValue),
    });
    props.onChange?.(form.getFieldsValue(), index);
    setMode(fieldsValue?.operator);
  };

  useEffect(() => {
    setMode(fieldsValue?.operator);
    form.setFieldsValue({
      ...fieldsValue,
      unit: getUnit(fieldsValue),
    });
  }, []);

  return (
    <Form
      preserve
      form={form}
      className={styles.customForm}
      onFieldsChange={onFieldsChange}
      style={{ display: 'flex' }}
    >
      <Space size={12}>
        <Input.Group compact>
          <Form.Item name='name' noStyle>
            <Select
              style={{ width: 220 }}
              className={styles.column}
              options={metricOptions}
              placeholder='请选择'
              showSearch
              defaultActiveFirstOption
            />
          </Form.Item>
          <Form.Item name='operator' noStyle>
            <Select
              style={{ width: 72 }}
              options={operatorOptions}
              className={styles.operator}
              placeholder=''
              defaultActiveFirstOption
            />
          </Form.Item>
          {mode !== MatchMode.Between && <Form.Item name='values' noStyle>
            <InputNumber controls={false} style={{ width: 200 }} />
          </Form.Item>}
          {mode === MatchMode.Between && <Form.Item name='values' noStyle>
            <TdsInputNumberRange style={{ width: 200 }} className='asset-range' />
          </Form.Item>}
          <Form.Item name='unit' noStyle>
            <Select
              style={{ width: 66 }}
              options={unitOptions}
              className={styles.operator}
              placeholder='单位'
              defaultActiveFirstOption
            />
          </Form.Item>
        </Input.Group>
        <CloseOutlined className={styles.close} onClick={onDelete}/>
      </Space>
    </Form>
  );
};

export const TdsCustomFields = (props: PropsWithStyles<TdsCustomFieldsProps>) => {
  const [metricOptions, setMetricOptions] = useState<DefaultOptionType[]>(props.metricSelectProps?.options || []);

  const defaultFilter = {
    name: metricOptions?.[0]?.value as string,
    operator: props.operatorOptions?.[0]?.value as MatchMode,
    unit: props.unitOptions?.[0]?.value as string,
  };
  const [value, setValue] = useControllableValue<TdsCustomFieldsValue[]>(props, {
    defaultValue: [defaultFilter],
  });

  const getFilterText = (values: TdsCustomFieldsValue[]) => values.map((item) => {
    const operatorText = MATCH_MODE_TEXT[item.operator as MatchMode];
    const currentKey = metricOptions?.find(metric => metric.value === item.name);

    // @ts-ignore
    const displayValue = item.operator === MatchMode.Between ? `[${item.values?.min}, ${item.values?.max}]` : item.values;
    return `${currentKey?.label} ${operatorText} ${displayValue}${item.unit}`;
  }).join(' 且 ');

  const [customFilters, setCustomFilters] = useState<TdsCustomFieldsValue[]>(value);
  // const [filterText, setFilterText] = useState('');
  const [visible, setVisible] = useState(false);
  const forms = useRef<FormInstance[]>([]);
  const [state] = useUrlState();

  const showOverlay = () => setVisible(true);

  const addFilter = () => {
    setCustomFilters([
      ...customFilters,
      defaultFilter,
    ]);
  };

  const FieldItemContainer = (itemProps: {
    index: number,
    onDelete?: (index: number) => void
  }) => {
    const { index, onDelete } = itemProps;
    const [form] = Form.useForm();

    if (!forms.current[index]) {
      forms.current[index] = form;
      forms.current[index].setFieldsValue(customFilters[index]);
    }

    return (
      <CustomFieldsItem
        form={forms.current[index]}
        index={index}
        onDelete={onDelete}
        operatorOptions={props.operatorOptions}
        unitOptions={props.unitOptions}
        metricOptions={metricOptions}
        fieldsValue={customFilters[index]}
      />
    );
  };

  const onDelete = (index: number) => {
    const tempData = [...customFilters];
    const tempForms = [...forms.current];
    remove(tempData, (_: TdsCustomFieldsValue, i: number) => index === i);
    remove(tempForms, (_: FormInstance, i: number) => index === i);
    forms.current = tempForms;
    setCustomFilters(tempData);
  };

  const renderFilters = () => customFilters.map((_filter, index) => (
      <FieldItemContainer index={index} onDelete={onDelete} />
  ));

  const isEmptyValues = (value: any) => {
    if (isNumber(value)) {
      return value < 0;
    } if (isString(value)) {
      return !value;
    }

    return isNil(value) || isEmpty(value);
  };

  const filterEmptyValues = (fieldValues: TdsCustomFieldsValue[]) => fieldValues
    .filter(item => item.name
      && item.operator
      && item.unit
      && !isEmptyValues(item.values));

  const onConfirm = () => {
    try {
      const formItemValues = forms.current.map(form => form.getFieldsValue());
      const values = filterEmptyValues(formItemValues);
      setVisible(false);
      setValue(values);
      // setFilterText(getFilterText(values));
      // setCustomFilters(values);
    } catch (e) {

    }
  };

  const queryOptions = async () => {
    const {
      forceRemote = false,
      options = [],
      getOptions,
    } = props.metricSelectProps ?? {};
    if (forceRemote) {
      const field = await getOptions?.();
      setMetricOptions(field || []);
    } else {
      setMetricOptions(options);
    }
  };

  const filterText = useMemo(() => {
    const formatValues = filterEmptyValues(value);
    if (!formatValues || isEmpty(formatValues)) return '';
    return getFilterText(value);
  }, [value, metricOptions]);

  useEffect(() => {
    setCustomFilters(value);
    // const formatValues = filterEmptyValues(value);
    // setFilterText(getFilterText(formatValues));
  }, [value, metricOptions]);

  useEffect(() => {
    queryOptions();
  }, [state.token]);

  const overlay = (
  <div className={styles.tdsCustomOverlay}>
      <span>以下的关系为全且</span>
      <div className={styles.tdsItemContainer}>
        {renderFilters()}
      </div>
      <div>
        <Space className={styles.tdsAddButton} size={3}>
          <PlusCircleOutlined/>
          <span onClick={addFilter}>添加筛选</span>
        </Space>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={() => setVisible(false)}>取消</Button>
          <Button type={'primary'} onClick={onConfirm}>确定</Button>
        </Space>
      </div>
  </div>
  );

  return (
    <div className={classNames(styles.tdsCustomFields, props.className)} style={props.style}>
      <Tooltip title={filterText}>
        <TdsInput
          placeholder='请选择'
          onFocus={showOverlay}
          suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
          style={{ width: '100%' }}
          value={filterText}
        />
      </Tooltip>
      <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={visible => setVisible(visible)}
        >
          <div style={{ height: 1 }}/>
        </Dropdown>
    </div>
  );
};
